import { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ChevronRight, ChevronDown } from "lucide-react";
import VestaInfo from "@/components/VestaInfo";
import VestaBadge from "@/components/VestaBadge";

const itDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const pt = (v) => (v != null ? v.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—");

export default function PuntiVesta() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    api.get("/vesta/yearly").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);

  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addr = (p) => [p.address, [p.cap, p.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—";
  const saldoCls = (v) => (v > 0 ? "text-emerald-500" : v < 0 ? "text-red-500" : "");

  return (
    <div className="space-y-6" data-testid="punti-vesta-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tight text-[#E3B341]"><VestaBadge className="h-9 w-9" /> Punti Vesta <VestaInfo /></h1>
      </div>
      <Card className="p-4">
        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Partner</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead>Agenzia di riferimento</TableHead>
                  <TableHead>Data attivazione</TableHead>
                  <TableHead className="text-right">Guadagnati</TableHead>
                  <TableHead className="text-right">Spesi</TableHead>
                  <TableHead className="text-right"><span className="inline-flex items-center justify-end gap-1 text-[#E3B341]"><VestaBadge /> Saldo Vesta <VestaInfo /></span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => {
                  const isOpen = open.has(p.id);
                  const hasYears = (p.years || []).length > 0;
                  return (
                    <Fragment key={p.id}>
                      <TableRow data-testid={`vesta-row-${p.id}`} className={hasYears ? "cursor-pointer" : ""} onClick={() => hasYears && toggle(p.id)}>
                        <TableCell>{hasYears && (isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />)}</TableCell>
                        <TableCell><Link to={`/partners/${p.id}`} onClick={(e) => e.stopPropagation()} className="font-semibold hover:text-primary hover:underline" data-testid={`vesta-partner-link-${p.id}`}>{p.name}</Link></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{addr(p)}</TableCell>
                        <TableCell className="text-sm">{p.agency_name || "—"}</TableCell>
                        <TableCell className="text-sm">{itDate(p.data_attivazione)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#E3B341]">{pt(p.tot_guadagnati)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground">{pt(p.tot_spesi)}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${saldoCls(p.tot_saldo)}`} data-testid={`vesta-saldo-${p.id}`}>{pt(p.tot_saldo)}</TableCell>
                      </TableRow>
                      {isOpen && hasYears && (
                        <TableRow key={`${p.id}-years`} data-testid={`vesta-years-${p.id}`}>
                          <TableCell />
                          <TableCell colSpan={7} className="bg-muted/30">
                            <div className="overflow-hidden rounded-md border border-border">
                              <table className="w-full text-sm">
                                <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                                  <tr>
                                    <th className="px-3 py-1.5 text-left font-semibold">Anno</th>
                                    <th className="px-3 py-1.5 text-right font-semibold">Valore ordini</th>
                                    <th className="px-3 py-1.5 text-right font-semibold">Guadagnati</th>
                                    <th className="px-3 py-1.5 text-right font-semibold">Spesi</th>
                                    <th className="px-3 py-1.5 text-right font-semibold">Saldo</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.years.map((y) => (
                                    <tr key={y.anno} className="border-t border-border" data-testid={`vesta-year-${p.id}-${y.anno}`}>
                                      <td className="px-3 py-1.5 font-semibold">{y.anno}</td>
                                      <td className="px-3 py-1.5 text-right font-mono">{eur(y.valore)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-[#E3B341]">{pt(y.guadagnati)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{pt(y.spesi)}</td>
                                      <td className={`px-3 py-1.5 text-right font-mono font-bold ${saldoCls(y.saldo)}`}>{pt(y.saldo)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
                {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-12 text-center text-muted-foreground">Nessun partner.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
